import { useEffect, useRef } from 'react';
import { useCanvasInteraction } from '@/features/canvas/useCanvasInteraction';
import { CanvasInteractionProvider } from '@/features/canvas/CanvasInteractionContext';
import { useViewportObserver } from '@/hooks/useViewportObserver';
import { WorldBackground } from './renderers/WorldBackground';
import { WorldLayer } from './renderers/WorldLayer';
import { ObjectLayer } from './renderers/ObjectLayer';
import { InteractionLayer } from './renderers/InteractionLayer';
import { OverlayLayer } from './renderers/OverlayLayer';
import { ConnectionLayer } from '@/components/connections/ConnectionLayer';
import { useInteractionStore } from '@/store/interactionStore';
import styles from './SpatialCanvas.module.css';

/**
 * The world viewport. Composes four renderers and owns pointer capture:
 *
 *   WorldBackground   canvas grid, screen space       (camera → redraw)
 *   WorldLayer        CSS-transformed world container (camera → transform)
 *     ConnectionLayer SVG edges between object centres
 *     ObjectLayer     DOM objects, world space
 *     InteractionLayer transient gesture feedback + selection handles
 *   OverlayLayer      screen-space chrome above the world
 *
 * This component itself never re-renders on camera movement.
 */
export const SpatialCanvas = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  useViewportObserver(rootRef);
  const controller = useCanvasInteraction(rootRef);

  // Cursor follows the gesture mode; written to the DOM directly so no subtree re-renders.
  useEffect(() => {
    const apply = (mode: string) => {
      rootRef.current?.setAttribute('data-mode', mode);
    };
    apply(useInteractionStore.getState().mode);
    return useInteractionStore.subscribe((s, prev) => {
      if (s.mode !== prev.mode) apply(s.mode);
    });
  }, []);

  return (
    <CanvasInteractionProvider value={controller}>
      <div ref={rootRef} className={styles.root} data-testid="spatial-canvas" {...controller.handlers}>
        <WorldBackground />
        <WorldLayer>
          <ConnectionLayer />
          <ObjectLayer />
          <InteractionLayer />
        </WorldLayer>
        <OverlayLayer />
      </div>
    </CanvasInteractionProvider>
  );
};
